import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Table, TableHead, TableBody,
  TableRow, TableCell, TableContainer,
  CircularProgress, Typography, Stack, Button,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { lightBlue } from '@mui/material/colors';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { SERVER_URL } from '../../constants';
import { formatLocalDateOnly } from '../shared utils/dateFormatters.js';
import { downloadAllergiesCsv, exportAllergiesPdf } from './allergiesExport';
import { hasRealAllergy } from './allergyFilters';

const ALL_CAMPUSES = 'ALL';

export default function AllergySummaryByCampus({
  // App always passes the selected year; this only covers standalone use, so
  // fall back to now rather than to a year that quietly goes stale.
  selectedYear = String(new Date().getFullYear()),
  // keep these optional if you ever want to override manually
  startDate: startDateProp,
  endDate: endDateProp,
}) {
  const [allergies, setAllergies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCampus, setSelectedCampus] = useState(ALL_CAMPUSES);

  // Derive start/end from selectedYear unless explicitly overridden
  const { startDate, endDate } = useMemo(() => {
    if (startDateProp && endDateProp) {
      return { startDate: startDateProp, endDate: endDateProp };
    }
    const y = String(selectedYear || new Date().getFullYear());
    return {
      startDate: `${y}-01-01`,
      endDate: `${y}-12-31`,
    };
  }, [selectedYear, startDateProp, endDateProp]);

  useEffect(() => {
    const token = sessionStorage.getItem('bearer');

    fetch(`${SERVER_URL}api/allergies?start=${startDate}&end=${endDate}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) =>
        // 204 means no allergies for this selection, not a failure.
        res.status === 204 ? [] : res.ok ? res.json() : Promise.reject(res.statusText))
      .then((data) => {
        setAllergies(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err);
        setLoading(false);
      });
  }, [startDate, endDate]);

  const groupedByCampus = useMemo(() => {
    const grouped = allergies
      .filter((row) => hasRealAllergy(row.allergies))
      .reduce((acc, row) => {
        if (!acc[row.campus]) acc[row.campus] = [];
        acc[row.campus].push(row);
        return acc;
      }, {});

    // Order each campus by arrival date (ascending), blanks last.
    Object.values(grouped).forEach((rows) => {
      rows.sort((a, b) => {
        const aTime = a.startDate ? new Date(a.startDate).getTime() : Infinity;
        const bTime = b.startDate ? new Date(b.startDate).getTime() : Infinity;
        return aTime - bTime;
      });
    });

    return grouped;
  }, [allergies]);

  const campusOptions = useMemo(
    () => Object.keys(groupedByCampus).sort((a, b) => a.localeCompare(b)),
    [groupedByCampus]
  );

  // If the chosen campus disappears (e.g. year change), fall back to All.
  useEffect(() => {
    if (selectedCampus !== ALL_CAMPUSES && !campusOptions.includes(selectedCampus)) {
      setSelectedCampus(ALL_CAMPUSES);
    }
  }, [campusOptions, selectedCampus]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  if (error) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><Typography color="error">Failed to load allergy data: {error}</Typography></Box>;
  }

  const visibleGrouped =
    selectedCampus === ALL_CAMPUSES
      ? groupedByCampus
      : groupedByCampus[selectedCampus]
      ? { [selectedCampus]: groupedByCampus[selectedCampus] }
      : {};

  const scopeLabel = selectedCampus === ALL_CAMPUSES ? '' : selectedCampus;
  const hasData = Object.keys(visibleGrouped).length > 0;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
      <Box sx={{ width: '100%', maxWidth: 1600 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={1}
          sx={{ mb: 1 }}
        >
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Campus</InputLabel>
            <Select
              label="Campus"
              value={selectedCampus}
              onChange={(e) => setSelectedCampus(e.target.value)}
            >
              <MenuItem value={ALL_CAMPUSES}>All campuses</MenuItem>
              {campusOptions.map((campus) => (
                <MenuItem key={campus} value={campus}>
                  {campus}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() => downloadAllergiesCsv(visibleGrouped, selectedYear, scopeLabel)}
              disabled={!hasData}
            >
              Export CSV
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<PictureAsPdfIcon />}
              onClick={() => exportAllergiesPdf(visibleGrouped, selectedYear, scopeLabel)}
              disabled={!hasData}
            >
              Export PDF
            </Button>
          </Stack>
        </Stack>

        <TableContainer component={Paper}>
        <Table size="small" aria-label="Allergy Summary Table">
          <TableHead>
            <TableRow sx={{ bgcolor: lightBlue[100], border: 1.5 }}>
              <TableCell align="center" sx={{ border: 1.5 }}><strong>First Name</strong></TableCell>
              <TableCell align="center" sx={{ border: 1.5 }}><strong>Last Name</strong></TableCell>
              <TableCell align="left" sx={{ border: 1.5 }}><strong>Allergies</strong></TableCell>
              <TableCell align="center" sx={{ border: 1.5 }}><strong>Type</strong></TableCell>
              <TableCell align="center" sx={{ border: 1.5 }}><strong>Arrival Date</strong></TableCell>
              <TableCell align="center" sx={{ border: 1.5 }}><strong>Departure Date</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!hasData && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No allergies to show for this selection.
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {Object.entries(visibleGrouped).map(([campus, rows]) => (
              <React.Fragment key={campus}>
                <TableRow>
                  <TableCell colSpan={6} sx={{ bgcolor: lightBlue[50], fontWeight: 'bold' }}>
                    {campus} · {rows.length}
                  </TableCell>
                </TableRow>

                {rows.map((row, i) => (
                  <TableRow key={`${campus}-${i}`}>
                    <TableCell align="center">{row.firstName}</TableCell>
                    <TableCell align="center">{row.lastName}</TableCell>
                    <TableCell align="left">{row.allergies}</TableCell>
                    <TableCell align="center">{row.personType}</TableCell>
                    <TableCell align="center">{formatLocalDateOnly(new Date(row.startDate))}</TableCell>
                    <TableCell align="center">{formatLocalDateOnly(new Date(row.endDate))}</TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      </Box>
    </Box>
  );
}